/**
 * @fileOverview Ctrl+click (⌘+click on macOS) on anything that navigates opens the target in a new tab.
 *
 * `<router-link>` and every Vuetify component given a `to` render a real `<a href>`, so the browser
 * already does this for them. Everything else navigates from a click handler — table rows, the command
 * palette, audit entries, the personnel planning context menus, chart cells — and a modifier key means
 * nothing to `router.push`. Teaching each of those call sites about the event would have to be
 * repeated by every new one, so the router does it once instead: a push that happens while a
 * modified click (or Ctrl+Enter) is being handled opens the resolved URL in a new tab and leaves the
 * current page where it is.
 *
 * "While being handled" means before the next task. A handler that navigates only after awaiting a
 * request — save, then go to the detail page — has left that window and navigates normally, so a
 * Ctrl+click on a save button never throws the result into another tab.
 *
 * Only `push` is covered: `replace` rewrites the current entry (a tab hash, a filter), which is state
 * of this page, not a destination.
 */

import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"

let newTabIntent = false

const markIntent = () => {
  newTabIntent = true
  setTimeout(() => (newTabIntent = false))
}

/** Registered in the capture phase, so a handler that stops propagation cannot hide the modifier. */
const onClick = event => {
  if (event.button === 0 && (event.ctrlKey || event.metaKey)) markIntent()
}

const onKeydown = event => {
  if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) markIntent()
}

export const initializeNewTabIntent = router => {
  // The Erklärung's frame cannot open windows (services/viewHelp/isolation.ts); a modified click there
  // would navigate nowhere at all, so it keeps navigating in place.
  if (IS_VIEW_HELP_EMBED) return

  window.addEventListener("click", onClick, true)
  window.addEventListener("keydown", onKeydown, true)

  const push = router.push.bind(router)
  router.push = to => {
    if (!newTabIntent) return push(to)
    newTabIntent = false
    window.open(router.resolve(to).href, "_blank", "noopener")
    return Promise.resolve()
  }
}
